/**
 * SN_SAI_GDE_REG_001 — AI 답변설정 (거래처) (2026-09-18, 2차 검토 확정)
 *
 * 회사 프롬프트(sai_firmguide) — 이 회사 모든 사람의 AI 답변에 붙는 "AI 가 답할 때 지킬 문장". 순서(no_seq)가 뒤에 있는 것이 이긴다.
 * 적용우선순위(SAI025) 회사(FIRM) 면 사원 개인 설정으로 못 바꾼다. 사원(USER) 이면 개인 설정이 있으면 그쪽.
 *
 * 조회조건 · 그리드 컬럼 · 폼 · 팝업 정의는 DB(sys_formctrl · sys_formgridcolumn · sys_popup*, 08_AI_화면설정.sql)에서 온다. 조회는 sys_query sn_sai_gde_reg_001_01.
 * 본 : 사용자 등록 SN_SYS_AUT_REG_004 (조회 + 그리드 1)
 */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { menuInfo } from "@shared/type/CommonType";

interface Props {
  Menuprops: menuInfo;
}

const SN_SAI_GDE_REG_001 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
  });

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormData(rtn.formData);
        setFormControlData(rtn.formControlData);
        setColums({
          grid1: rtn.grid.grid1 || [],
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch, onInsert, onDelete, onSave,
  }));

  const onSearch = async () => {
    if (
      gridRefs.current["grid1"].current.getChange().modifiedRowcount > 0
    ) {
      const confirm = await showDialog("confirmCancel", "수정된 내용이 있습니다. 저장하시겠습니까?");
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) return;
      } else if (confirm === undefined) {
        return;
      }
    }
    try {
      /* 다시 읽기 전에 수정 표시를 지운다. 안 지우면 아니오를 눌러도 "수정 중" 으로 남는다 */
      gridRefs.current["grid1"].current?.resetUpdate?.();
      const params = {
        id: "sn_sai_gde_reg_001_01",
        params: searchRef.current.getFormdata(),
      };
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data?.[0] || [],
      });
    } catch (error: any) {}
  };

  const onInsert = async () => {
    try {
      gridRefs.current["grid1"].current?.addRow();
    } catch (error: any) {}
  };
  const onDelete = async () => {
    if (gridRefs.current["grid1"].current?.getDisplayedRowCount() < 1) return;
    const confirm = await showDialog("confirm", "선택한 행을 삭제 표시합니다. 저장하면 지워집니다.");
    if (!confirm) return;
    gridRefs.current["grid1"].current?.deleteSelectedRows();
  };
  const onSave = async () => {
    try {
      gridRefs.current["grid1"].current.stopEditing();
      if (!gridRefs.current["grid1"].current.requiredValuesCheck()) return false;
      const sai_firmguideChanges = gridRefs.current["grid1"].current.getChange();
      if (sai_firmguideChanges.modifiedRowcount === 0) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }
      const saveData: any = {
        sai_firmguide: sai_firmguideChanges,
      };
      const ok = await fnSave("SN_SAI_GDE_REG_001_SAVE_01", saveData);
      if (!ok) return false;
      gridRefs.current["grid1"].current.resetUpdate();
      await onSearch();
      return true;
    } catch (error: any) {
      gridRefs.current["grid1"].current?.resetUpdate(true);
      return false;
    }
  };
  /** 행추가 기본값 — 회사 · 다음 순서. 고정값(사용여부 등)은 sys_formgridcolumn.cd_value 가 넣는다 */
  const onBeforeAddGuide = (): any => {
    const rows: any[] = [];
    gridRefs.current["grid1"].current?.forEachFilteredNode((node: any) => {
      if (node.data) rows.push(node.data);
    });
    const nextSeq = rows.reduce((mx, r) => Math.max(mx, Number(r.no_seq) || 0), 0) + 1;
    return { cd_firm: userInfo.cd_firm, no_seq: nextSeq };
  };
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onBeforeAddRow: onBeforeAddGuide
        },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel ref={searchRef} data={formData} controls={formControlData} />
      </div>
      <div className="w-full flex h-full min-h-0 space-x-1.5">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{ rowSelection: "single", title: "답변설정" }}
            events={grids.grid1.events}
          />
      </div>
    </div>
  );
});

export default SN_SAI_GDE_REG_001;
